import { AccessibleLottie } from '@/components/AccessibleLottie';
import { useHubContextColor } from '@/components/Hub/useHubContextColor';
import { useHubIconTransition } from '@/components/Hub/useHubIconTransition';
import { cn } from '@/lib/utils';

export interface HubIconProps {
    className?: string;
}

/**
 * How much wider the transition art is than the static it stands in for. A static is the bare glyph, a transition the same glyph on a larger canvas.
 */
const TRANSITION_SIZE = `${(47 / 35) * 100}%`;

/**
 * The Hub glyph. Its color follows the current section of the app, animating between colors on navigation.
 * Presentational only since it always sits inside a labeled control.
 */
export const HubIcon = ({ className }: HubIconProps) => {
    const targetColor = useHubContextColor();
    const { staticSrc, animationData, onAnimationComplete } = useHubIconTransition(targetColor);

    return (
        <span className={cn('relative block size-full', className)}>
            <AccessibleLottie
                // Remount per animation so a second transition restarts from frame 0 rather than resuming a finished one.
                key={animationData ? 'transition' : 'static'}
                animationData={animationData ?? undefined}
                fallbackSrc={staticSrc}
                alt=""
                presentational
                loop={false}
                onComplete={onAnimationComplete}
                // Sized rather than scaled, so the renderer draws the canvas at the size it is shown at.
                className={animationData
                    ? 'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2'
                    : 'size-full'}
                style={animationData ? { width: TRANSITION_SIZE, height: TRANSITION_SIZE } : undefined}
            />
        </span>
    );
};
